import React, { useState } from 'react';
import { Button, Modal, Box, Typography, CircularProgress, List, ListItem, ListItemText, ListItemIcon, Divider } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTheme } from '../../contexts/ThemeContext';

export const ProcessStepModal = ({
  isOpen,
  onClose,
  title,
  message,
  currentItems = [],
  errorItems = [],
  warningItems = [],
  step,
  canRetry = true,
  canRemove = true,
  onRetry,
  onContinue,
  onCancel
}) => {
  const { theme } = useTheme();
  const [showDetails, setShowDetails] = useState(false);

  // Group errors by reason for better display
  const getErrorSummary = () => {
    const errorReasons = {};

    // Combine both error and warning items
    [...errorItems, ...warningItems].forEach(item => {
      const reasons = item.unpublishedReasons?.reason || ["Unknown error"];
      reasons.forEach(reason => {
        errorReasons[reason] = (errorReasons[reason] || 0) + 1;
      });
    });

    return Object.entries(errorReasons)
      .sort((a, b) => b[1] - a[1])  // Sort by frequency
      .slice(0, 5);  // Top 5 errors
  };

  const errorSummary = getErrorSummary();

  // Calculate success percentage correctly
  const totalItems = currentItems.length;

  const successPercentage = (100 - (((errorItems.length + warningItems.length) / totalItems) * 100)).toFixed(1);

  const styles = {
    modal: {
      position: 'absolute',
      top: '53%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      maxHeight: '80vh',
      bgcolor: theme.background.homeSection,
      boxShadow: 24,
      p: 4,
      borderRadius: 2,
      overflowY: 'auto',
      border: `2px solid ${theme.border.light}`
    },
    outlinedButton: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.color.primary}`,
      borderRadius: '10px',
      color: theme.color.primary,
    },
    containedButton: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      border: `1px solid ${theme.border.light}`,
      borderRadius: '10px',
      color: theme.text.negative,
    },
    primaryText: {
      color: theme.text.primary,
      fontFamily: theme.fontFamily,
    },
    secondaryText: {
      color: theme.accent.disabled,
      fontFamily: theme.fontFamily,
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => {/* Prevent accidental closing */ }}
      aria-labelledby="process-step-modal-title"
    >
      <Box sx={styles.modal}>
        <Typography id="process-step-modal-title" variant="h6" component="h2" sx={{ mb: 2, ...styles.primaryText, }}>
          {title}
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, ...styles.primaryText, }}>
          {message}
        </Typography>

        {/* Stats section */}
        <Box sx={{ mb: 3, p: 2, bgcolor: theme.background.secondary, borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, ...styles.primaryText, }}>
            Processing Results:
          </Typography>
          <Typography variant="body2" sx={styles.primaryText}>
            ✅ Success: {errorItems.length + warningItems.length === 0 ? 'All items' : `${successPercentage}%`}
          </Typography>
          {warningItems.length > 0 && (
            <Typography variant="body2" sx={styles.primaryText}>
              ⚠️ Warnings: {warningItems.length} items {canRemove && '(will be removed)'}
            </Typography>
          )}
          {errorItems.length > 0 && (
            <Typography variant="body2" sx={styles.primaryText}>
              ❌ Errors: {errorItems.length} items {canRemove && '(will be removed)'}
            </Typography>
          )}
        </Box>

        {/* Error summary section */}
        {errorSummary.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.primaryText, }}>
              Common Errors:
            </Typography>
            <List dense>
              {errorSummary.map(([reason, count], index) => (
                <ListItem key={index}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {reason.toLowerCase().includes('error') ?
                      <ErrorOutlineIcon sx={{ color: theme.accent.error, }} /> :
                      <WarningAmberIcon sx={{ color: theme.accent.warning, }} />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={styles.primaryText}>
                        {reason} ({count} items)
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowDetails(!showDetails)}
              sx={{ mt: 1, ...styles.outlinedButton }}
            >
              {showDetails ? 'Hide Details' : 'Show Affected Items'}
            </Button>
          </Box>
        )}

        {/* Detailed items section - shown when "Show Details" is clicked */}
        {showDetails && (
          <Box sx={{ mb: 3, maxHeight: '200px', overflowY: 'auto' }}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, ...styles.primaryText }}>
              Error Items:
            </Typography>
            <List dense>
              {errorItems.map((item, index) => (
                <ListItem key={`error-${index}`}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ErrorOutlineIcon sx={{ color: theme.accent.error, }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" sx={styles.primaryText}>{item.sku || item.itemId || `Item ${index + 1}`}</Typography>}
                    secondary={<Typography variant="body2" sx={styles.secondaryText}>{item.unpublishedReasons?.reason?.[0] || "Unknown error"}</Typography>}
                  />
                </ListItem>
              ))}
              {errorItems.length === 0 && (
                <ListItem>
                  <ListItemText primary={<Typography variant="body2" sx={styles.primaryText}>No error items</Typography>} />
                </ListItem>
              )}
            </List>

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, ...styles.primaryText }}>
              Warning Items:
            </Typography>
            <List dense>
              {warningItems.map((item, index) => (
                <ListItem key={`warning-${index}`}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <WarningAmberIcon sx={{ color: theme.accent.warning, }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" sx={styles.primaryText}>{item.sku || item.itemId || `Item ${index + 1}`}</Typography>}
                    secondary={<Typography variant="body2" sx={styles.secondaryText}>{item.unpublishedReasons?.reason?.[0] || "Unknown warning"}</Typography>}
                  />
                </ListItem>
              ))}
              {warningItems.length === 0 && (
                <ListItem>
                  <ListItemText primary={<Typography variant="body2" sx={styles.primaryText}>No warning items</Typography>} />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3
        }}>
          {canRetry && (
            <Button
              variant="outlined"
              onClick={onRetry}
              startIcon={<CircularProgress size={16} />}
              sx={styles.outlinedButton}
            >
              Retry Step
            </Button>
          )}

          {canRemove && (errorItems.length > 0 || warningItems.length > 0) && (
            <Button
              variant="contained"
              sx={{ ...styles.containedButton, backgroundColor: theme.accent.warning, }}
              onClick={onContinue}
            >
              Continue Without Failed Items
            </Button>
          )}

          <Button
            variant="contained"
            sx={{ ...styles.containedButton, backgroundColor: canRemove ? theme.accent.error : theme.color.primary, }}
            onClick={onCancel}
          >
            {canRemove ? "Cancel Process" : "Close"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}